import {Component} from "react"
import ApiHandler from "src/api/ApiHandler"
import Loader from "src/components/general/Loading"
import ErrorHandler from "src/components/errors/ErrorHandler"
import UserVisibleErrorStoringDecorator from "src/components/decorators/UserErrorStoringDecorator"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import UserInputError from "src/validation/exception_messages/UserInputError"
import ServerError from "src/validation/exception_messages/ServerError"
import ThrottleLimitExceededError from "src/validation/exception_messages/ThrottleLimitExceededError"
import QuotaLimitExceededError from "src/validation/exception_messages/QuotaLimitExceededError"
import "./SaleEnquiryPage.css"
import StateConstants from "src/states/StateContstants"

interface SaleEnquiryPageProps {
    apiHandler: ApiHandler
    loader: Loader
    errorHandler: ErrorHandler
    stateHandler: EventAdaptingStateHandler
    enquiryType: "SALE" | "VALUATION"
    initialAddress: string
}

interface SaleEnquiryPageState {
    name: string
    email: string
    phoneNumber: string
    requestCallback: boolean
    address: string
    urgency: string
    additionalInformation: string
    enquiryReason: string
    isSubmitting: boolean
    submissionSuccess: boolean
    isMobile: boolean
}

declare global {
    interface Window {
        gtag?: (...args: any[]) => void
    }
}

class SaleEnquiryPage extends Component<SaleEnquiryPageProps, SaleEnquiryPageState> {
    apiHandler: ApiHandler
    loader: Loader
    errorHandler: ErrorHandler
    stateHandler: EventAdaptingStateHandler
    enquiryType: "SALE" | "VALUATION"
    initialAddress: string

    constructor(props: SaleEnquiryPageProps) {
        super(props)
        this.initialAddress = props.initialAddress
        this.apiHandler = props.apiHandler
        this.loader = props.loader
        this.errorHandler = props.errorHandler
        this.stateHandler = props.stateHandler
        this.enquiryType = props.enquiryType
        
        this.submitForm = this.submitForm.bind(this)
        this.handleInputChange = this.handleInputChange.bind(this)
        
        this.state = {
            name: "",
            email: "",
            phoneNumber: "",
            requestCallback: false,
            address: this.initialAddress,
            urgency: "MEDIUM",
            additionalInformation: "",
            enquiryReason: this.enquiryType === "SALE" ? "SELLING" : "CURIOUS",
            isSubmitting: false,
            submissionSuccess: false,
            isMobile: false
        }
        this.stateHandler.addListener(StateConstants.ERROR, () => {
            this.setState({})
        })
    }

    componentDidMount() {
        this.fireGoogleAdsPageLoadConversionEvent()
    }

    fireGoogleAdsPageLoadConversionEvent() {
        // Low-value conversion on page load
        // Avoid crashing if gtag isn't loaded (e.g. local dev, tests, ad-blockers).
        if (typeof window === "undefined" || typeof window.gtag !== "function") return

        // Avoid firing multiple times per tab/session (e.g. React StrictMode dev remounts).
        const storageKey = "saleEnquiryPage_google_ads_page_load_conversion_fired"
        try {
            if (window.sessionStorage?.getItem(storageKey) === "1") return
            window.sessionStorage?.setItem(storageKey, "1")
        } catch {
            // Ignore storage access errors; still attempt to fire once.
        }

        window.gtag("event", "conversion", {
            send_to: "AW-17859474557/UX1XCLDn294bEP3oh8RC",
            value: 0.5,
            currency: "EUR"
        })
    }

    gtagReportConversion(url?: string) {
        // Event snippet for Page view conversion page
        // Call gtag_report_conversion when someone clicks on the chosen link or button.
        // (Here: after a successful form submission)
        if (typeof window === "undefined" || typeof window.gtag !== "function") return false

        const callback = function () {
            if (typeof url !== "undefined") {
                window.location.href = url
            }
        }

        window.gtag("event", "conversion", {
            send_to: "AW-17859474557/UX1XCLDn294bEP3oh8RC",
            value: 5.0,
            currency: "EUR",
            event_callback: callback
        })

        return false
    }

    handleInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        const { name, value } = event.target
        this.setState({
            [name]: value
        } as any)
    }


    getTitle() {
        return this.enquiryType === "SALE" ? "Sell My Home" : "Get Professional Valuation"
    }

    getDescription() {
        if (this.enquiryType === "SALE") {
            return (
                <div>
                    Sell your home with our trusted partners. 
                    <br/>
                    We work with leading Irish auctioneers and estate agents to help get you the best offer for your home.
                </div>
            )
        }
        return (
            <div>
                <p>Request an onsite valuation for your property to get the most accurate appraisal for your home. We work with leading Irish auctioneers and estate agents to help get you the best offer for your home.</p>
            </div>
        )
    }

    @UserVisibleErrorStoringDecorator()
    async submitForm(event: React.FormEvent) {
        event.preventDefault()

        // Clear any previous error so users get immediate feedback on retry.
        this.stateHandler.set("error", undefined)
        
        if (!this.state.name || !this.state.name.trim()) {
            this.stateHandler.set("error", new UserInputError("Name is required"))
            return
        }
        
        if (!this.state.email || !this.state.email.trim()) {
            this.stateHandler.set("error", new UserInputError("Email is required"))
            return
        }

        if (!this.state.enquiryReason || !this.state.enquiryReason.trim()) {
            this.stateHandler.set("error", new UserInputError("Please select an enquiry reason"))
            return
        }

        this.setState({ isSubmitting: true })

        try {
            const input: any = {
                contact_details: {
                    name: this.state.name,
                    email: this.state.email,
                    phone_number: this.state.phoneNumber,
                    request_callback: this.state.requestCallback
                },
                enquiry_type: this.enquiryType,
                enquiry_reasons: [this.state.enquiryReason],
                urgency: this.state.urgency,
                additional_information: this.state.additionalInformation
            }

            if (this.state.address && this.state.address.trim()) {
                input.address = {
                    id: "address_id",
                    a: this.state.address
                }
            }

            await this.apiHandler.submitSaleEnquiry(input)

            // Google Ads conversion event on successful submit
            this.gtagReportConversion()
            
            this.setState({ 
                submissionSuccess: true,
                isSubmitting: false,
                name: "",
                email: "",
                phoneNumber: "",
                requestCallback: false,
                address: "",
                urgency: "MEDIUM",
                additionalInformation: "",
                enquiryReason: this.enquiryType === "SALE" ? "SELLING" : "CURIOUS"
            })

            setTimeout(() => {
                this.setState({ submissionSuccess: false })
            }, 5000)
        } catch (error) {
            console.error("Error submitting sale enquiry:", error)
            // Preserve known user-visible error types (esp. 4XX => UserInputError via Validator)
            if (
                error instanceof UserInputError ||
                error instanceof ThrottleLimitExceededError ||
                error instanceof QuotaLimitExceededError ||
                error instanceof ServerError
            ) {
                this.stateHandler.set("error", error)
            } else {
                // Fallback: still show something user-friendly
                this.stateHandler.set(
                    "error",
                    new ServerError("We couldn't submit your enquiry right now. Please try again in a moment.")
                )
            }
            this.setState({ isSubmitting: false })
        }
    }

    render() {
        if (this.state.isSubmitting) {
            return this.loader.render("Submitting your enquiry...")
        }

        return (
            <div className="info-page-container">
                <div className="info-content-container">
                    <div className="enquiry-header">
                        <h1 className="info-title">{this.getTitle()}</h1>
                        <p className="info-description">
                            {this.getDescription()}
                        </p>
                    </div>
                    
                    {this.state.submissionSuccess && (
                        <div className="success-message">
                            ✅ Thank you! Your enquiry has been submitted successfully. We'll be in touch soon.
                        </div>
                    )}

                    {this.errorHandler.render(this.stateHandler.get("error"), {
                    onDismiss: () => this.stateHandler.set("error", null)
                })}

                    <form className="sale-enquiry-form" onSubmit={this.submitForm}>
                        <div className="form-section">
                            <h2 className="form-section-title">Contact Information</h2>
                            <div className="form-group">
                                <label htmlFor="name" className="form-label">Full Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className="form-input"
                                    value={this.state.name}
                                    onChange={this.handleInputChange}
                                    placeholder="John Smith"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email" className="form-label">Email *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-input"
                                    value={this.state.email}
                                    onChange={this.handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phoneNumber" className="form-label">Phone Number *</label>
                                <input
                                    type="tel"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    className="form-input"
                                    value={this.state.phoneNumber}
                                    onChange={this.handleInputChange}
                                    placeholder="+353 83 345 6789"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <h2 className="form-section-title">Enquiry Details</h2>
                            <div className="form-group">
                                <label className="form-label">Reason for Enquiry *</label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="enquiryReason"
                                            value="SELLING"
                                            checked={this.state.enquiryReason === "SELLING"}
                                            onChange={this.handleInputChange}
                                        />
                                        Looking to sell
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="enquiryReason"
                                            value="BUYING"
                                            checked={this.state.enquiryReason === "BUYING"}
                                            onChange={this.handleInputChange}
                                        />
                                        Looking to buy
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="enquiryReason"
                                            value="CURIOUS"
                                            checked={this.state.enquiryReason === "CURIOUS"}
                                            onChange={this.handleInputChange}
                                        />
                                        Just curious
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="enquiryReason"
                                            value="OTHER"
                                            checked={this.state.enquiryReason === "OTHER"}
                                            onChange={this.handleInputChange}
                                        />
                                        Other
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h2 className="form-section-title">Property Details</h2>
                            <div className="form-group">
                                <label htmlFor="address" className="form-label">Property Address *</label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    className="form-input"
                                    value={this.state.address}
                                    onChange={this.handleInputChange}
                                    placeholder="Enter your property address"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <h2 className="form-section-title">Additional Information</h2>
                            <div className="form-group">
                                <label htmlFor="additionalInformation" className="form-label">Message</label>
                                <textarea
                                    id="additionalInformation"
                                    name="additionalInformation"
                                    className="form-input form-textarea"
                                    rows={4}
                                    value={this.state.additionalInformation}
                                    onChange={this.handleInputChange}
                                    placeholder="Tell us more about your property or requirements..."
                                />
                            </div>
                        </div>

                        <button type="submit" className="submit-button">
                            Submit Enquiry
                        </button>
                    </form>
                </div>
            </div>
        )
    }
}

export default SaleEnquiryPage

